import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import '../style/title.css';
import ParticlesComponent from '../components/particles';
import Projects from '../components/Projects.tsx';
import Experiences from '../components/Experiences.tsx';
import transition from '../lib/transition.js';


const LandingPage: React.FC = () => {
  const location = useLocation();

  useLayoutEffect(() => {
    if (location.state && location.state.section) {
      const sectionId = location.state.section;
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location.state]);

  const wavesBackground = {
    backgroundImage: `url('/images/background/layered-waves-2.svg')`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top center',
  };

  return (
    <div className="min-h-screen text-gray-200">

      {/* Hero */}
      <div className="h-[75vh] md:h-[100vh] w-full relative overflow-hidden">
        <ParticlesComponent id="particles-hero" className="absolute inset-0 z-0" />

        {/* Pulsing orange glow */}
        <motion.div
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-1/2 z-[1] -translate-x-1/2 -translate-y-1/2"
          style={{
            width: '80vw',
            height: '80vw',
            maxWidth: 820,
            maxHeight: 820,
            background:
              'radial-gradient(circle, rgba(255,69,0,0.20) 0%, rgba(255,165,0,0.09) 38%, rgba(0,18,32,0) 70%)',
          }}
          animate={{ scale: [1, 1.15, 1], opacity: [0.55, 1, 0.55] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />

        {/* Heading */}
        <div className="absolute inset-0 z-10 flex flex-col justify-start pt-[20vh] md:pt-[26vh] md:px-72 px-6">
          <motion.div
            animate={{ y: [0, -12, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.12, delayChildren: 0.2 } } }}
            >
              <motion.p
                className="mb-3 md:mb-4 text-center md:text-left text-xs md:text-sm uppercase tracking-[0.35em] text-orange-400"
                variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0 } }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              >
                Welcome to my portfolio
              </motion.p>
              <motion.h1
                aria-label="HI THERE, SCROLL DOWN"
                className="flex flex-wrap justify-center md:justify-start gap-x-3 md:gap-x-4 font-bold text-5xl md:text-6xl"
                style={{ perspective: '800px' }}
              >
                {'HI THERE, SCROLL DOWN'.split(' ').map((word, i) => (
                  <motion.span
                    key={i}
                    className="highlight inline-block"
                    variants={{
                      hidden: { opacity: 0, y: 50, rotateX: -60 },
                      visible: { opacity: 1, y: 0, rotateX: 0 },
                    }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.h1>
            </motion.div>
          </motion.div>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-[23%] z-5 overflow-hidden">
          <img src="/images/background/layered-waves.svg" alt="Layered Waves" className="w-full h-full object-cover object-bottom transform" />
        </div>
      </div>

      {/* About */}
      <div style={wavesBackground} className="w-full relative z-10 ">
        <section id="about" className="mx-auto pt-16 md:pt-20 text-center">
          <h2 className="text-3xl font-bold mb-8">About Me</h2>
          <div className="flex flex-col items-center">
            <p className="text-white text-3xl md:text-5xl font-bold">
              MUHAMMAD <span className='text-orange-400'>ARIEF</span>
            </p>
            <h5 className="text-white text-3xl md:text-5xl font-bold mb-4 md:mb-14 md:hidden"><span className='text-orange-400'>BRAJA </span>PUTRA</h5>
            <div className="flex flex-col md:flex-row items-center">
              <img src="/images/default-profile.jpg" alt="Arief Braja profile" className="rounded-full w-36 h-36 md:w-52 md:h-52 object-cover shadow-md mx-4" />
              <p className='text-start'>
                <h5 className="text-white text-3xl md:text-5xl font-bold mb-4 md:mb-14 hidden md:block"><span className='text-orange-400'>BRAJA </span>PUTRA</h5>
                <h5 className='text-white text-xl md:text-2xl mt-4 md:mt-0'>i just like to code </h5>
                <h5 className='text-white text-xl md:text-2xl mb-12'>and explore new things :D</h5>
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Experiences & Projects */}
      <div style={{ backgroundColor: '#001220' }} className="w-full relative z-10">
        <section id="experiences" className="container mx-auto max-w-5xl px-4 md:px-8 lg:px-16 pt-16 md:pt-20">
          <h2 className="text-3xl font-bold mb-12 text-center">Experiences</h2>
          <Experiences />
        </section>

        <section id="projects" className="mx-4 2xl:mx-80 px-6 pt-16 md:pt-20 pb-16 md:pb-24">
          <Projects />
        </section>

        <div className="w-full h-40 md:h-56 overflow-hidden">
          <img src="/images/background/layered-waves.svg" alt="Layered Waves" className="w-full h-full object-cover object-bottom transform" />
        </div>
      </div>

      {/* Contact */}
      <div style={wavesBackground} className="w-full relative z-10">
        <section id="contact" className="container mx-auto px-4 py-16 md:py-20 text-center">
          <h2 className="text-3xl font-bold mb-6">Contact Me</h2>
          <div className="flex flex-col gap-2">
            <a href="https://www.linkedin.com/in/ariefbraja"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 font-semibold text-xl">
              LinkedIn
            </a>
            <a href="mailto:marief.braja@gmail.com"
              className="text-blue-500 hover:text-blue-700 font-semibold text-xl">
              Email
            </a>
          </div>
        </section>
      </div>
    </div>
  );
};

export default transition(LandingPage);
